import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { NavigationRoute } from "src/routes";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useNavigate } from "react-router";
import type * as TabsPrimitive from "@radix-ui/react-tabs";
import { Input } from "./input";
import { useTranslation } from "react-i18next";

const List = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <Card className={cn("flex flex-col", className)} {...props} ref={ref} />
    ),
);

const ListHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <CardHeader
            className={cn("flex flex-row justify-between", className)}
            {...props}
            ref={ref}
        />
    ),
);

const ListTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        return (
            <CardTitle
                className={cn("text-xl leading-normal font-bold", className)}
                {...props}
                ref={ref}
            />
        );
    },
);

const ListDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <CardDescription
            className={cn("text-muted-foreground text-sm", className)}
            {...props}
            ref={ref}
        />
    ),
);

type ListDrilldownProps = React.PropsWithChildren & {
    to: NavigationRoute;
};

const ListDrilldown = ({ to }: ListDrilldownProps): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    return (
        <Button className="mt-0" variant={"outline"} onClick={() => navigate(to.path())}>
            {t("component.list.drillDown")}
            <ArrowRight />
        </Button>
    );
};

const ListContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        return (
            <CardContent
                className={cn("flex flex-col overflow-hidden", className)}
                {...props}
                ref={ref}
            />
        );
    },
);

const ListTabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Tabs>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Tabs>
>(({ className, ...props }, ref) => (
    <Tabs ref={ref} className={cn("flex flex-col overflow-hidden", className)} {...props} />
));

const ListTabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.TabsContent>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.TabsContent>
>(({ className, ...props }, ref) => (
    <TabsContent ref={ref} className={cn("flex flex-col overflow-hidden", className)} {...props} />
));

const ListFilters = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("mt-2 mb-6 flex gap-4", className)} {...props} />
    ),
);

const ListSearchInput = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
    ({ className, type, ...props }, ref) => {
        const { t } = useTranslation();
        return (
            <Input
                ref={ref}
                className={cn("flex gap-4", className)}
                {...props}
                type={type}
                placeholder={t("component.list.filters.search")}
            />
        );
    },
);

export {
    List,
    ListHeader,
    ListTitle,
    ListDescription,
    ListDrilldown,
    ListTabs,
    ListTabsContent,
    ListContent,
    ListFilters,
    ListSearchInput,
};
